import { type AxiosRequestConfig, AxiosError } from 'axios';
import { ShipmentResponse } from '../../interfaces/Shipment/Response/shipmentResponse';
import axiosInstance from '../../utils/axiosInstance';

const headerToString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const MAX_REPORT_ERROR_MESSAGE_LENGTH = 300;

export class ShipmentReportDownloadError extends Error {
  readonly status?: number;
  readonly backendMessage?: string;

  constructor(message: string, status?: number, backendMessage?: string) {
    super(message);
    this.name = 'ShipmentReportDownloadError';
    this.status = status;
    this.backendMessage = backendMessage;
  }
}

const safeReportErrorMessage = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (
    !normalized ||
    normalized.length > MAX_REPORT_ERROR_MESSAGE_LENGTH ||
    /[<>]/.test(normalized)
  ) {
    return undefined;
  }
  return normalized;
};

const readBlobText = async (blob: Blob): Promise<string> => {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
};

const reportErrorEnvelopeMessage = (value: unknown): string | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const envelope = value as {
    message?: unknown;
    detail?: unknown;
    data?: { message?: unknown; detail?: unknown } | null;
  };
  return [
    envelope.message,
    envelope.detail,
    envelope.data?.message,
    envelope.data?.detail,
  ]
    .map(safeReportErrorMessage)
    .find((message): message is string => Boolean(message));
};

const parseReportErrorBody = async (
  responseData: unknown
): Promise<string | undefined> => {
  if (responseData instanceof Blob) {
    try {
      const text = await readBlobText(responseData);
      if (!text || text.length > 10_000) return undefined;
      return reportErrorEnvelopeMessage(JSON.parse(text));
    } catch {
      return undefined;
    }
  }
  if (typeof responseData === 'string') {
    try {
      return reportErrorEnvelopeMessage(JSON.parse(responseData));
    } catch {
      return safeReportErrorMessage(responseData);
    }
  }
  return reportErrorEnvelopeMessage(responseData);
};

const normalizeShipmentReportError = async (
  error: unknown
): Promise<ShipmentReportDownloadError> => {
  const structured =
    error && typeof error === 'object'
      ? (error as {
          status?: unknown;
          responseData?: unknown;
          response?: { status?: unknown; data?: unknown };
        })
      : undefined;
  const statusValue = structured?.status ?? structured?.response?.status;
  const status =
    typeof statusValue === 'number' && Number.isFinite(statusValue)
      ? statusValue
      : undefined;
  const responseData = structured?.responseData ?? structured?.response?.data;
  const backendMessage = await parseReportErrorBody(responseData);
  const fallback =
    error instanceof Error ? safeReportErrorMessage(error.message) : undefined;
  return new ShipmentReportDownloadError(
    backendMessage || fallback || 'Failed to download report',
    status,
    backendMessage
  );
};

// Helper function to get company language setting from cookies
const getCompanyLanguage = (): string => {
  try {
    const cookieSettings = document.cookie
      .split('; ')
      .find((row) => row.startsWith('settings='));

    if (cookieSettings) {
      const settings = JSON.parse(
        decodeURIComponent(cookieSettings.split('=')[1])
      );
      return settings.language || 'ENGLISH';
    }
    return 'ENGLISH';
  } catch (error) {
    console.error('Error getting company language:', error);
    return 'ENGLISH';
  }
};

// Helper function to generate localized filename
const getLocalizedFilename = (
  originalFilename: string,
  language: string
): string => {
  const fileExtension = originalFilename.split('.').pop() || '';
  const baseFilename = originalFilename.replace(`.${fileExtension}`, '');

  // Extract shipment ID if it exists in the filename pattern
  const shipmentIdMatch =
    baseFilename.match(/shipment[_-]?report[_-]?(.+)/) ||
    baseFilename.match(/shipment[_-]?download[_-]?(.+)/);

  if (shipmentIdMatch) {
    const shipmentId = shipmentIdMatch[1];
    const templates: { [key: string]: string } = {
      ENGLISH: `shipment_report_${shipmentId}.${fileExtension}`,
      SPANISH: `reporte_envio_${shipmentId}.${fileExtension}`,
      spanish: `reporte_envio_${shipmentId}.${fileExtension}`, // Handle lowercase
    };
    return templates[language] || templates['ENGLISH'];
  }

  // If no shipment ID pattern found, just return original filename
  return originalFilename;
};

export interface GetAllShipmentsRequest {
  company_id: string;
  status?: 'CURRENT' | 'COMPLETED' | 'ARCHIVED';
  search?: string;
  page_number?: number;
  page_size?: number;
  rtl_shipment_filter?: {
    shipment_statuses?: string[];
    estimated_time_of_arrival_days?: [number, number];
    alerts?: boolean;
    eta_start_date?: string;
    eta_end_date?: string;
  };
}

export interface GetAllShipmentsResponse {
  statusCode: number;
  data: {
    statusCode: number;
    data: ShipmentResponse[];
    message: string;
    error: string | null;
    total_pages: number;
    total_items: number;
    page_number: number;
  };
  message: string;
  error: string | null;
}

export interface CreateShipment {
  company_id: string;
  name: string;
  waybill_no?: string | null;
  goods_description?: string | null;
  sender_name?: string | null;
  receiver_name?: string | null;
  start_location_id?: string | null;
  end_location_id?: string | null;
  loading_date?: string | null;
  start_date?: string | null;
  status?: string | null;
  estimated_duration?: number | null;
  tracking_service?: string | null;
  vessel_name?: string | null;
  container_number?: string | null;
  carrier_code?: string | null;
  bol_number?: string | null;
  location_alerts?: boolean;
  additional_alert_emails?: string[];
  rtl_ids?: string[];
  device_ids?: string[];
}

export interface ShipmentLocation {
  id: string;
  latitude: string;
  longitude: string;
  address: string;
  date_created: string;
  date_updated: string;
}

export interface ShipmentStatusHistory {
  id: string;
  shipment_id: string;
  status: string;
  date_created: string;
}

export interface CreateShipmentResponse {
  statusCode: number;
  data: {
    id: string;
    company_id: string;
    name: string;
    waybill_no: string;
    sender: string;
    receiver: string;
    goods_description: string;
    start_location_id: string;
    end_location_id: string;
    loading_date: string;
    start_date: string;
    estimated_duration: number;
    tracking_service: number;
    vessel_name: string;
    location_alerts: boolean;
    additional_alert_emails: string[];
    date_created: string;
    date_updated: string;
    shipment_status_history: ShipmentStatusHistory[];
    start_location: ShipmentLocation;
    end_location: ShipmentLocation;
    shipment_intel_link?: {
      status?: string;
      [key: string]: unknown;
    };
  };
  message: string;
  error: string | null;
}
export interface UpdateShipment {
  id: string;
  name?: string | null;
  waybill_no?: string | null;
  goods_description?: string | null;
  sender_name?: string | null;
  receiver_name?: string | null;
  start_location_id?: string | null;
  end_location_id?: string | null;
  loading_date?: string | null;
  start_date?: string | null;
  status?: string | null;
  estimated_duration?: number | null;
  tracking_service?: string | null;
  vessel_name?: string | null;
  additional_alert_emails?: string[];
  location_alerts?: boolean | null;
  rtl_configs?: Array<{
    rtl_id: string;
    profile_id?: string | null;
    delayed_start?: number | null;
    recording_interval?: number | null;
    reporting_interval?: number | null;
  }>;
}

export interface AssignRTLShipment {
  shipment_id: string;
  rtl_ids: string[];
}

export interface ReleaseRTLShipment {
  shipment_id: string;
  rtl_ids: string[];
}

export interface DeleteShipmentDto {
  id: string;
}

export interface GetShipmentDownload {
  shipment_id?: string;
  rtl_id?: string;
  device_id?: string;
  date_from?: string;
  date_to?: string;
  file_type: 'csv' | 'pdf';
}

export interface ChangeShipmentStatus {
  id: string;
  status: string;
}

export const getAllShipments = async (data: GetAllShipmentsRequest) => {
  const response = await axiosInstance.post('/shipment/get-all', data);
  return response.data;
};

export const getAllShipmentsRTL = async (
  data: GetAllShipmentsRequest
): Promise<GetAllShipmentsResponse> => {
  if (!data.rtl_shipment_filter) {
    try {
      const response = await axiosInstance.get('/shipments', {
        params: {
          company_id: data.company_id,
          status: data.status || '',
          search: data.search || '',
          limit: data.page_size,
          cursor: encodePageCursor(data.page_number),
          include: 'devices,warnings',
        },
      });
      return adaptCanonicalShipmentsResponse(response.data, data);
    } catch (error) {
      console.warn(
        'Canonical shipments endpoint unavailable, falling back to legacy route.',
        error
      );
    }
  }

  const response = await axiosInstance.post('/shipment/get-all-with-rtl', data);
  return response.data;
};

const encodePageCursor = (pageNumber?: number): string | undefined => {
  if (!pageNumber || pageNumber <= 1) return undefined;
  const payload = JSON.stringify({ page: pageNumber });
  if (typeof btoa === 'function') {
    return btoa(payload);
  }
  return undefined;
};

const asShipmentArray = (value: unknown): ShipmentResponse[] => {
  if (Array.isArray(value)) return value as ShipmentResponse[];
  if (value && typeof value === 'object') {
    const record = value as {
      data?: unknown;
      items?: unknown;
      shipments?: unknown;
    };
    if (Array.isArray(record.data)) return record.data as ShipmentResponse[];
    if (Array.isArray(record.items)) return record.items as ShipmentResponse[];
    if (Array.isArray(record.shipments))
      return record.shipments as ShipmentResponse[];
    if (record.data && typeof record.data === 'object') {
      return asShipmentArray(record.data);
    }
  }
  return [];
};

const numberFrom = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const adaptCanonicalShipmentsResponse = (
  response: unknown,
  request: GetAllShipmentsRequest
): GetAllShipmentsResponse => {
  const envelope = (response || {}) as {
    statusCode?: number;
    data?: {
      items?: unknown;
      pagination?: {
        limit?: number;
        has_more?: boolean;
      };
    };
    message?: string;
    error?: string | null;
  };
  const canonicalData = envelope.data || {};
  const rawItems = canonicalData.items;
  const legacyShape =
    rawItems && typeof rawItems === 'object'
      ? (rawItems as {
          statusCode?: number;
          message?: string;
          error?: string | null;
          total_pages?: number;
          total_items?: number;
          page_number?: number;
          data?: unknown;
        })
      : {};
  const shipments = asShipmentArray(rawItems);
  const pageNumber = request.page_number || legacyShape.page_number || 1;
  const pageSize =
    request.page_size ||
    canonicalData.pagination?.limit ||
    shipments.length ||
    50;
  const hasMore = Boolean(canonicalData.pagination?.has_more);
  const totalItems = numberFrom(
    legacyShape.total_items,
    hasMore ? pageNumber * pageSize + 1 : shipments.length
  );
  const totalPages = numberFrom(
    legacyShape.total_pages,
    hasMore ? pageNumber + 1 : Math.max(1, pageNumber)
  );

  return {
    statusCode: envelope.statusCode || 200,
    data: {
      statusCode: legacyShape.statusCode || 200,
      data: shipments,
      message: legacyShape.message || envelope.message || '',
      error: legacyShape.error ?? envelope.error ?? null,
      total_pages: totalPages,
      total_items: totalItems,
      page_number: pageNumber,
    },
    message: envelope.message || 'Shipments retrieved successfully',
    error: envelope.error ?? null,
  };
};

export const changeShipmentStatus = async (data: ChangeShipmentStatus) => {
  const response = await axiosInstance.post('/shipment/change-status', data);
  return response.data;
};

export const archiveShipment = async (shipment_id: string) => {
  const response = await axiosInstance.post('/shipment/change-status', {
    id: shipment_id,
    status: 'ARCHIVED',
  });
  return response.data;
};

export const createShipment = async (data: CreateShipment) => {
  const response = await axiosInstance.post('/shipment/create', data);
  return response.data;
};

export const updateShipment = async (data: UpdateShipment) => {
  const response = await axiosInstance.post('/shipment/update', data);
  return response.data;
};

export const assignRTLToShipment = async (data: AssignRTLShipment) => {
  const response = await axiosInstance.post('/shipment/assign-rtl', data);
  return response.data;
};

export const releaseRTLFromShipment = async (data: ReleaseRTLShipment) => {
  const response = await axiosInstance.post('/shipment/release-rtl', data);
  return response.data;
};

export interface ShipmentDevicesRequest {
  shipment_id: string;
  device_ids: string[];
}

export interface ShipmentDevicesResponse {
  statusCode: number;
  data: {
    devices: import('../../types/Shipmentdetails').AttachedDevice[];
  };
  message: string;
  error: string | null;
}

export const attachDevicesToShipment = async (
  data: ShipmentDevicesRequest
): Promise<ShipmentDevicesResponse> => {
  const response = await axiosInstance.put('/shipment/attach-devices', data);
  return response.data;
};

export const detachDevicesFromShipment = async (
  data: ShipmentDevicesRequest
): Promise<ShipmentDevicesResponse> => {
  const response = await axiosInstance.put('/shipment/detach-devices', data);
  return response.data;
};

export const deleteShipment = async (data: DeleteShipmentDto) => {
  const response = await axiosInstance.post('/shipment/delete', data);
  return response.data;
};

export const getShipmentDownload = async (
  data: GetShipmentDownload,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await axiosInstance.post(
      '/shipment/get-shipment-download',
      data,
      {
        ...config,
        responseType: 'blob',
        // Remove transformResponse completely to let axios handle it
      }
    );

    if (typeof response.status === 'number' && response.status >= 400) {
      throw await normalizeShipmentReportError({
        status: response.status,
        responseData: response.data,
      });
    }

    if (response.data) {
      const contentType =
        headerToString(response.headers['content-type']) ||
        'application/octet-stream';
      let mimeType = contentType.split(';')[0];

      const contentDisposition = headerToString(
        response.headers['content-disposition']
      );
      let filename = 'shipment-download';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      } else {
        const fileType = data.file_type || 'pdf';
        if (fileType === 'csv' || mimeType.includes('csv')) {
          filename = `shipment-download-${Date.now()}.csv`;
          mimeType = 'text/csv';
        } else if (fileType === 'pdf' || mimeType.includes('pdf')) {
          filename = `shipment-download-${Date.now()}.pdf`;
          mimeType = 'application/pdf';
        } else {
          filename = `shipment-download-${Date.now()}.${fileType}`;
        }
      }

      // Apply localized filename based on company language settings
      const companyLanguage = getCompanyLanguage();
      filename = getLocalizedFilename(filename, companyLanguage);

      const blob = new Blob([response.data], { type: mimeType });

      const url = window.URL.createObjectURL(blob);

      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }

    return response;
  } catch (error: unknown) {
    if (error instanceof ShipmentReportDownloadError) throw error;
    if (error instanceof AxiosError && !error.response) {
      throw new ShipmentReportDownloadError('Failed to download report');
    }
    throw await normalizeShipmentReportError(error);
  }
};

export const downloadShipmentCsvTemplate = async () => {
  try {
    const response = await axiosInstance.get(
      '/shipment/download-csv-template',
      {
        responseType: 'blob',
      }
    );

    if (response.data) {
      const contentDisposition = headerToString(
        response.headers['content-disposition']
      );
      let filename = 'Shipment_Bulk_Upload_Template.csv';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }

    return response;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      if (error.response) {
        return error.response;
      }
      throw new Error('Failed to download CSV template');
    } else {
      throw error;
    }
  }
};

export interface BulkUploadShipmentsCsvRequest {
  file: File;
  company_id: string;
  user_id: string;
}

export const getShipmentInformation = async (shipmentId: string) => {
  const response = await axiosInstance.post(
    '/shipment/get-shipment-information',
    {
      shipment_id: shipmentId,
    }
  );
  return response.data; // { statusCode, data, message, error }
};

export const bulkUploadShipmentsCsv = async (
  data: BulkUploadShipmentsCsvRequest
) => {
  try {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('company_id', data.company_id);
    formData.append('user_id', data.user_id);

    const response = await axiosInstance.post(
      '/shipment/bulk-upload-csv',
      formData,
      {
        responseType: 'blob',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (response.data) {
      const contentDisposition = headerToString(
        response.headers['content-disposition']
      );
      let filename = 'shipment_upload_results.csv';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }

    return response;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      if (error.response) {
        // Try to parse error message from response
        const contentType = headerToString(
          error.response.headers['content-type']
        );
        if (contentType && contentType.includes('application/json')) {
          // If response is JSON, try to parse it
          try {
            const text = await new Response(error.response.data).text();
            const errorData = JSON.parse(text);
            if (errorData && errorData.message) {
              throw new Error(errorData.message);
            }
          } catch {
            // If parsing fails, use default message
          }
        }
        throw new Error('Failed to upload shipments CSV');
      }
      throw new Error('Failed to upload shipments CSV');
    } else {
      throw error;
    }
  }
};
